/**
 * PaymentMethodSelector — visual radio cards. Highlights the active option
 * with the brand color and shows logos/microcopy for each method.
 */
import React from 'react';
import { Smartphone, Banknote, CreditCard, Wallet } from 'lucide-react';
import { Badge, css } from '../../design';

export type PaymentMethodId = 'upi' | 'cod' | 'card' | 'wallet';

export interface PaymentMethodSelectorProps {
  value: PaymentMethodId;
  onChange: (v: PaymentMethodId) => void;
}

interface OptDef {
  id: PaymentMethodId;
  label: string;
  sub: string;
  icon: React.ReactNode;
  comingSoon?: boolean;
}

const OPTIONS: OptDef[] = [
  { id: 'upi',     label: 'UPI / GPay / PhonePe', sub: 'Instant · Recommended', icon: <Smartphone size={18} /> },
  { id: 'cod',     label: 'Cash on Delivery',     sub: 'Pay when it arrives',   icon: <Banknote size={18} /> },
  { id: 'card',    label: 'Credit / Debit Card',  sub: 'Visa · Mastercard · Rupay', icon: <CreditCard size={18} />, comingSoon: true },
  { id: 'wallet',  label: 'Wallet',               sub: 'Use your balance',      icon: <Wallet size={18} />, comingSoon: true },
];

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({ value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {OPTIONS.map((opt) => {
      const active = value === opt.id;
      const disabled = !!opt.comingSoon;
      return (
        <button
          key={opt.id}
          type="button"
          onClick={() => !disabled && onChange(opt.id)}
          disabled={disabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            background: active ? css.color.primarySoft : css.color.surface,
            border: `2px solid ${active ? css.color.primary : css.color.border}`,
            borderRadius: 12,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            textAlign: 'left',
            transition: 'background 160ms ease, border-color 160ms ease',
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 999,
              border: `2px solid ${active ? css.color.primary : css.color.borderStrong}`,
              background: active ? css.color.primary : 'transparent',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            {active && (
              <span
                style={{
                  position: 'absolute',
                  inset: 3,
                  background: '#FFFFFF',
                  borderRadius: 999,
                }}
              />
            )}
          </span>
          <span style={{ color: active ? css.color.primary : css.color.text }}>{opt.icon}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: css.color.text, fontWeight: 700, fontSize: 14 }}>
              {opt.label}
              {opt.comingSoon && <Badge variant="neutral" size="sm">Coming soon</Badge>}
            </span>
            <span style={{ display: 'block', fontSize: 12, color: css.color.textMuted, marginTop: 2 }}>
              {opt.sub}
            </span>
          </span>
        </button>
      );
    })}
  </div>
);

export default PaymentMethodSelector;

